import { useState, useEffect, useCallback } from "react";
import { useConditionalPolling } from "../../hooks/useConditionalPolling.js";
import { 
  Paper, 
  Group, 
  Stack, 
  Badge, 
  Button, 
  Text, 
  Accordion, 
  Loader, 
  Center,
  Alert,
  Divider,
  ThemeIcon,
  Code,
  Box,
  Checkbox,
} from "@mantine/core";
import { 
  History, 
  Files, 
  AlertCircle,
  FileText,
  RotateCw,
  Image as ImageIcon,
  ExternalLink,
  Eye,
  StopCircle,
} from "lucide-react";
import { modals } from "@mantine/modals";
import { showSuccess, showError, showWarning } from "../../utils/notifications";
import { EmailLog, ProcessStep, getFileUrl, ReprocessOptions } from "../../services/logService.js";
import { getStatusColor, getStatusLabel, formatTimestamp } from "../../utils/formatters.js";
import { StepTimeline } from "./StepTimeline.js";
import { POLLING_INTERVAL_MS } from "../../constants/app";

/**
 * 添付ファイルアイテム（単体）
 * プレビュー読み込みロジックを含む
 */
function AttachmentItem({ attachment }: { attachment: EmailLog["attachments"][0] }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const isImage = attachment.mimeType.startsWith("image/");
  const isPdf = attachment.mimeType === "application/pdf";

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const loadPreview = async () => {
    if (previewUrl || loading) return;
    try {
      setLoading(true);
      const downloadUrl = await getFileUrl(attachment.storagePath);
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      if (isPdf) {
        const text = await blob.slice(0, 1024 * 100).text();
        const match = text.match(/\/Count\s+(\d+)/);
        if (match && match[1]) {
          const count = parseInt(match[1], 10);
          if (count > 0 && count < 100) setPageCount(count);
        }
      }

      const localUrl = URL.createObjectURL(blob);
      setPreviewUrl(localUrl);
    } catch (err) {
      console.error("Failed to load preview:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExternal = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const u = previewUrl || await getFileUrl(attachment.storagePath);
      window.open(u, "_blank", "noopener,noreferrer");
    } catch {
      alert("ファイルを開くのに失敗しました");
    }
  };

  return (
    <Accordion.Item value={attachment.storagePath}>
      <Accordion.Control onClick={loadPreview}>
        <Group justify="space-between" pr="md" wrap="nowrap">
          <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
            {isImage ? (
              <ImageIcon size={16} color="var(--mantine-color-blue-filled)" />
            ) : (
              <FileText size={16} color="var(--mantine-color-gray-6)" />
            )}
            <Text size="sm" fw={500} lineClamp={1}>{attachment.filename}</Text>
          </Group>
          <Group gap="xs">
            <Badge size="xs" variant="outline">{attachment.mimeType.split('/')[1]?.toUpperCase() || attachment.mimeType}</Badge>
            <Button
              component="div"
              variant="subtle"
              size="compact-xs"
              leftSection={<ExternalLink size={14} />}
              onClick={handleOpenExternal}
            >
              別タブ
            </Button>
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          {loading && (
            <Center py="md">
              <Loader size="sm" />
              <Text size="xs" ml="xs" c="dimmed">プレビューを準備中...</Text>
            </Center>
          )}
          
          {previewUrl && isImage && (
            <Center>
              <Paper withBorder p={4} radius="sm" bg="var(--mantine-color-dark-7)" style={{ width: "100%" }}>
                <img
                  src={previewUrl}
                  alt={attachment.filename}
                  style={{ width: "100%", height: "auto", borderRadius: "4px", display: "block" }}
                />
              </Paper>
            </Center>
          )}

          {previewUrl && isPdf && (
            <Paper withBorder style={{ 
              width: "100%", 
              aspectRatio: `1 / ${1.414 * pageCount}`, 
              position: "relative" 
            }}>
              <iframe
                src={`${previewUrl}#view=FitH&toolbar=0`}
                title={attachment.filename}
                width="100%"
                height="100%"
                style={{ border: "none", display: "block" }}
              />
            </Paper>
          )}

          {previewUrl && !isImage && !isPdf && (
            <Alert color="blue" variant="light" icon={<Eye size={16} />}>
              このファイル形式の直接プレビューには対応していません。別タブで開くか詳細を確認してください。
            </Alert>
          )}

          <Group justify="space-between">
            <Code style={{ fontSize: "10px", flex: 1 }}>{attachment.storagePath}</Code>
          </Group>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

interface EventDetailPanelProps {
  log: EmailLog;
  onRefresh?: () => void;
  fetchSteps: (logId: string) => Promise<ProcessStep[]>;
  reprocess: (logId: string, options?: ReprocessOptions) => Promise<{ success: boolean; message: string; newLogId?: string }>;
  abort?: (logId: string, reason?: string) => Promise<{ success: boolean; message: string }>;
}

export function EventDetailPanel({ log, onRefresh, fetchSteps, reprocess, abort }: EventDetailPanelProps) {
  const [steps, setSteps] = useState<ProcessStep[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [isAborting, setIsAborting] = useState(false);

  const loadSteps = useCallback(async (showLoading = true) => {
    if (!log.id) return;
    if (showLoading) setLoadingSteps(true);
    try {
      const fetchPromise = fetchSteps(log.id);

      // 手動更新時のみ最低待機時間(750ms)を設ける
      // [NOTE] テスト環境（NODE_ENV=test）ではこの遅延をスキップする。
      // 理由: 意図的な遅延（setTimeout）は、テスト実行時の非同期処理の完了待ち（waitForなど）と競合し、
      // タイムアウトエラーやFlakiness（不安定なテスト）の原因となるため。
      const waitPromise = showLoading && process.env.NODE_ENV !== 'test'
        ? new Promise(resolve => setTimeout(resolve, 750))
        : Promise.resolve();

      const [ data ] = await Promise.all([
        fetchPromise,
        waitPromise
      ]);

      setSteps(data);
    } catch (error) {
      console.error("Failed to fetch steps:", error);
    } finally {
      if (showLoading) setLoadingSteps(false);
    }
  }, [log.id, fetchSteps]);

  // log.id または log.status が変わったら steps を再取得
  // （ステータスがエラーに変わった際にも最新の steps を表示するため）
  useEffect(() => {
    loadSteps();
  }, [loadSteps, log.status]);

  // 処理中の場合、定期的に更新 (ローディング表示なし)
  useConditionalPolling(
    () => loadSteps(false), 
    POLLING_INTERVAL_MS, 
    ['processing', 'received'].includes(log.status)
  );

  const handleReprocess = () => {
    if (!log.id) return;
    const selected = new Set(log.attachments.map((_, index) => index));
    let publish = false;
    
    modals.openConfirmModal({
      title: 'ログの再処理',
      children: (
        <Stack>
          <Text size="sm">選択した原本を再解析します。元の履歴は保持されます。</Text>
          {log.attachments.map((attachment, index) => <Checkbox key={index} defaultChecked label={attachment.filename}
            onChange={event => { if (event.currentTarget.checked) selected.add(index); else selected.delete(index); }} />)}
          <Checkbox label="新しいCSVをクライアントへ配信し、メールも送信する" onChange={event => { publish = event.currentTarget.checked; }} />
          <Text size="sm" c="orange">再配信を選ぶと、取得済みの注文も別ファイルとして再配信されます。</Text>
        </Stack>
      ),
      labels: { confirm: '再処理を開始', cancel: 'キャンセル' },
      onConfirm: async () => {
        try {
          setIsReprocessing(true);
          if (!selected.size) throw new Error("対象の添付を選択してください");
          const result = await reprocess(log.id!, { attachmentIndexes: [...selected].sort((a, b) => a - b), publish });
          showSuccess(result.message, "再処理を受け付けました");
          onRefresh?.();
        } catch (error) {
          showError(error instanceof Error ? error.message : "不明なエラーが発生しました", "再処理に失敗しました");
        } finally {
          setIsReprocessing(false);
        }
      },
    });
  };

  // 処理中/保留中のログを手動で中断
  const handleAbort = () => {
    if (!log.id || !abort) return;
    
    modals.openConfirmModal({
      title: '処理の中断',
      children: (
        <Text size="sm">
          この処理を中断しますか？<br />
          ステータスは「中断」に変更されますが、既に実行中のバックエンド処理がある場合は停止されません。
        </Text>
      ),
      labels: { confirm: '中断する', cancel: 'キャンセル' },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          setIsAborting(true);
          await abort(log.id!);
          showWarning("ログのステータスが「中断」に変更されました。", "処理を中断しました");
          onRefresh?.();
        } catch (error) {
          showError(error instanceof Error ? error.message : "不明なエラーが発生しました", "中断に失敗しました");
        } finally {
          setIsAborting(false);
        }
      },
    });
  };

  // 中断ボタンを表示するかどうか
  const canAbort = abort && ['processing', 'pending', 'received'].includes(log.status);

  return (
    <Stack gap="lg">
      {/* ヘッダーエリア */}
      <Paper p="md" withBorder shadow="xs">
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ThemeIcon size="lg" color={getStatusColor(log.status)} variant="light">
              <FileText size={20} />
            </ThemeIcon>
            <Box>
              <Text fw={700} size="lg">
                {log.customerName || "不明な顧客"} {log.eventTypeName || "イベント"}
              </Text>
              <Text size="sm" c="dimmed">{log.from}</Text>
            </Box>
          </Group>
          <Group>
            {canAbort && (
              <Button 
                leftSection={<StopCircle size={16} />}
                variant="light"
                color="orange"
                onClick={handleAbort}
                loading={isAborting}
              >
                処理を中断
              </Button>
            )}
            <Button 
              leftSection={<RotateCw size={16} />}
              variant="light"
              onClick={handleReprocess}
              loading={isReprocessing}
              disabled={['processing', 'received'].includes(log.status)}
            >
              再処理を実行
            </Button>
            <Badge 
              size="lg" 
              color={getStatusColor(log.status)} 
              variant="filled"
              leftSection={['processing', 'received'].includes(log.status) ? <Loader size={12} color="white" type="oval" /> : null}
            >
              {getStatusLabel(log.status)}
            </Badge>
          </Group>
        </Group>
        <Divider mb="md" />
        {log.fileResults?.map(result => <Stack key={result.jobId} gap={2} mb="sm">
          <Text size="sm">{result.filename}：{({ queued: "受付済み", running: "処理中", retry: "再試行待ち", done: "完了", failed: "失敗", attention: "要確認" } as Record<string, string>)[result.state] ?? result.state}</Text>
          {result.error && <Text size="xs" c="red">{result.error}</Text>}
          <Text component="a" href={`/logs?logId=${encodeURIComponent(result.logId)}`} size="sm">添付の処理結果を開く</Text>
        </Stack>)}
        <Group gap="xl">
          <Box>
            <Text size="xs" c="dimmed">受信日時</Text>
            <Text size="sm">{formatTimestamp(log.receivedAt)}</Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">顧客ID</Text>
            <Code style={{ 
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              letterSpacing: "0.05em",
              padding: "2px 6px",
              fontWeight: 600
            }}>
              {log.customerId}
            </Code>
          </Box>
          {log.to && (
            <Box>
              <Text size="xs" c="dimmed">送信先</Text>
              <Code style={{ 
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                letterSpacing: "0.05em",
                padding: "2px 6px",
                fontWeight: 600
              }}>
                {log.to}
              </Code>
            </Box>
          )}
          <Box>
            <Text size="xs" c="dimmed">ログID</Text>
            <Code style={{ 
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              letterSpacing: "0.05em",
              padding: "2px 6px",
              fontWeight: 600
            }}>
              {log.id}
            </Code>
          </Box>
        </Group>
      </Paper>

      {/* エラーメッセージがある場合 */}
      {log.errorMessage && (
        <Alert icon={<AlertCircle size={16} />} title="エラー詳細" color="red">
          {log.errorMessage}
        </Alert>
      )}

      {/* 拒否理由がある場合（不正なToアドレス等） */}
      {log.rejectionReason && (
        <Alert icon={<AlertCircle size={16} />} title="拒否理由" color="orange">
          {log.rejectionReason}
        </Alert>
      )}

      {/* コンテンツエリア: 処理タイムライン */}
      <Stack gap="xs">
        <Group justify="space-between" mb="xs">
           <Group gap="xs">
            <History size={18} />
            <Text fw={600} size="sm">詳細処理タイムライン</Text>
            <Button 
              variant="subtle" 
              size="compact-xs" 
              onClick={() => loadSteps(true)}
              disabled={loadingSteps}
              leftSection={loadingSteps ? <Loader size={14} color="gray" /> : <RotateCw size={14} />}
              aria-label="タイムラインを更新"
              color="gray"
            >
              更新
            </Button>
          </Group>
          {loadingSteps && <Loader size="xs" />}
        </Group>
        <Paper withBorder p="lg" radius="sm">
          {loadingSteps ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : (
            <StepTimeline steps={steps} log={log} />
          )}
        </Paper>
      </Stack>

      {/* コンテンツエリア: 添付ファイル */}
      {log.attachments && log.attachments.length > 0 && (
        <Stack gap="xs">
          <Group gap="xs">
            <Files size={18} />
            <Text fw={600} size="sm">添付ファイル ({log.attachments.length})</Text>
          </Group>
          <Paper withBorder radius="sm" p="md">
             <Accordion variant="separated">
               {log.attachments.map((att, index) => (
                 <AttachmentItem key={`${att.storagePath}-${index}`} attachment={att} />
               ))}
             </Accordion>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}
